import * as React from 'react';
import { IScope, route, IRootScopeService, ILocationService } from 'angular';
import { JsonObject, EnhancedAppointment } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import type { CurrentUser } from '@/app-package';
import { ListingLabelContext, ListingLabelContextType } from 'contexts';
import type { User } from '@bb/users';
import { ListingDoc, OrgDoc } from '@bb/domain/dist/schemas';
import {
  Empty,
  Loading,
  Tabs,
  PanelHeader,
  PanelBody,
} from '../../react-common';
import AngularLocationServiceContext from './AngularLocationServiceContext';
import { AppointmentActionModalContextProvider } from '../../../contexts/modal/AppointmentActionModalContext';
import { AppointmentActionModal } from '../../../modals/AppointmentActionModal';
import AppointmentPropertyUpdate from './AppointmentPropertyUpdateContainer';
import AppointmentDetail from './AppointmentDetailContainer';
import ListingView from './ListingViewContainer';
import OfferView from './OfferViewContainer';
import { getCoopAppointmentById } from './APIService';

type ManageAppointmentPageProps = {
  $rootScope: IRootScopeService & {
    auth: CurrentUser;
    setChatVisibility: (visible: boolean) => void;
    isMobileView: boolean;
  } & Pick<
      ListingLabelContextType,
      'labelOverrideMap' | 'getAvailability' | 'getLabel'
    >;
  $scope: IScope;
  $routeParams: route.IRouteParamsService;
  $location: ILocationService;
  ToastService: any;
};

type ManageAppointmentPageState = JsonObject<{
  activeTabIndex: number;
  appointment: EnhancedAppointment;
  isLoading: boolean;
  listing: Omit<ListingDoc, 'reps' | 'org'> & { reps: User[]; org: OrgDoc };
}>;

class ManageAppointmentPage extends React.Component<
  ManageAppointmentPageProps,
  ManageAppointmentPageState
> {
  toastService: { add: (args: any[]) => void };

  readonly TABS = [
    { text: 'View Listing' },
    { text: 'Showing' },
    { text: 'Register Offer' },
    { text: 'Property Updates' },
  ];

  user: CurrentUser;

  constructor(props: ManageAppointmentPageProps) {
    super(props);

    const { $scope, $rootScope, ToastService } = this.props;

    this.state = {
      activeTabIndex: 1,
      isLoading: true,
      appointment: null,
      listing: null,
    };

    this.toastService = {
      add: (...args) => {
        $scope.$apply(() => {
          ToastService.add(...args);
        });
      },
    };

    this.user = $rootScope.auth;
  }

  async componentDidMount() {
    const {
      $routeParams: { appointmentId },
    } = this.props;

    try {
      const { appointment, listing } =
        await getCoopAppointmentById(appointmentId);
      this.setState({ appointment, isLoading: false, listing });
    } catch (err) {
      errorReporter.error(err);
      this.setState({ isLoading: false });
    }
  }

  handleOnTabSelect = (value: string, index: number) => () => {
    this.setState({ activeTabIndex: index });
  };

  updateAppointment = async (appointmentId: string) => {
    try {
      const { appointment } = await getCoopAppointmentById(appointmentId);
      this.setState({ appointment });
    } catch (err) {
      errorReporter.error(err);
    }
  };

  public render() {
    const { $location, $scope, ToastService, $rootScope } = this.props;
    const { isLoading, appointment, activeTabIndex, listing } = this.state;

    if (isLoading) return <Loading size="fa-5x" />;

    if (!appointment) return <Empty text="Invalid Page" />;

    return (
      <AngularLocationServiceContext.Provider
        value={{ $location, $scope, $rootScope, ToastService }}
      >
        <AppointmentActionModalContextProvider
          setChatVisibility={$rootScope.setChatVisibility}
          isMobileView={$rootScope.isMobileView}
        >
          <ListingLabelContext.Provider
            // @ts-ignore Other parts of provider are not needed here. Can likely remove this provider
            value={{
              labelOverrideMap: $rootScope.labelOverrideMap,
              getAvailability: $rootScope.getAvailability,
              getLabel: $rootScope.getLabel,
            }}
          >
            <AppointmentActionModal />
            <PanelHeader
              title={
                <>
                  <a href="/#/my_business" className="listing-link">
                    My Business
                  </a>
                  {' > Buyer Showing'}
                </>
              }
            />
            <PanelBody>
              <div style={{ width: '100%' }}>
                <div className="row">
                  <div className="col-xs-12">
                    <Tabs
                      options={this.TABS}
                      activeTabIndex={activeTabIndex}
                      onChange={this.handleOnTabSelect}
                    />
                  </div>
                </div>

                <hr style={{ marginTop: '10px', marginBottom: '10px' }} />

                {activeTabIndex === 0 && (
                  <ListingView auth={$rootScope.auth} listing={listing} />
                )}
                {activeTabIndex === 1 && (
                  <AppointmentDetail
                    appointment={appointment}
                    // @ts-expect-error: listing.org is not matching
                    listing={listing}
                    updateAppointment={this.updateAppointment}
                  />
                )}
                {activeTabIndex === 2 && (
                  <OfferView
                    appointmentId={appointment.id}
                    listing={listing}
                    user={this.user}
                  />
                )}
                {activeTabIndex === 3 && (
                  <AppointmentPropertyUpdate
                    appointment={appointment}
                    ToastService={this.toastService}
                  />
                )}
              </div>
            </PanelBody>
          </ListingLabelContext.Provider>
        </AppointmentActionModalContextProvider>
      </AngularLocationServiceContext.Provider>
    );
  }
}

export default ManageAppointmentPage;
